import { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  CircularProgress,
  Alert,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from '@mui/material';
import { Add as AddIcon, Edit as EditIcon, Delete as DeleteIcon, Security as SecurityIcon, Person as PersonIcon, Email as EmailIcon, Schedule as ScheduleIcon } from '@mui/icons-material';
import Layout from '../components/Layout';
import { useFilter } from '../contexts/FilterContext';
import { getRefreshPaperStyles, getRefreshTableStyles } from '../utils/refreshAnimation';
import { userService } from '../services/userService';
import type { User, UserRole } from '../types/user';
import { UserRole as UserRoleEnum } from '../types/user';
import { formatDateToTR } from '../utils/dateUtils';

const roleLabels: Record<UserRole, string> = {
  [UserRoleEnum.SystemAdmin]: 'Sistem Yöneticisi',
  [UserRoleEnum.TenantAdmin]: 'Tenant Yöneticisi',
  [UserRoleEnum.Viewer]: 'Görüntüleyici',
};

const Users = () => {
  const { refreshTrigger, autoRefresh, isRefreshing } = useFilter();
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const navigate = useNavigate();

  // Role değerini normalize et (backend'den string enum ismi olarak gelebilir)
  const normalizeRole = (role: UserRole | string | number): UserRole => {
    if (typeof role === 'number') {
      return role as UserRole;
    }
    if (typeof role === 'string') {
      const num = parseInt(role, 10);
      if (!isNaN(num) && (num === UserRoleEnum.SystemAdmin || num === UserRoleEnum.TenantAdmin || num === UserRoleEnum.Viewer)) {
        return num as UserRole;
      }
      // String enum isimlerini kontrol et
      const roleStr = role.trim();
      const roleLower = roleStr.toLowerCase();
      if (roleStr === 'SystemAdmin' || roleLower === 'systemadmin' || roleLower === 'system_admin') {
        return UserRoleEnum.SystemAdmin;
      } else if (roleStr === 'TenantAdmin' || roleLower === 'tenantadmin' || roleLower === 'tenant_admin') {
        return UserRoleEnum.TenantAdmin;
      } else if (roleStr === 'Viewer' || roleLower === 'viewer') {
        return UserRoleEnum.Viewer;
      }
    }
    return UserRoleEnum.Viewer; // Fallback
  };

  const loadUsers = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await userService.getAll();
      // Role değerlerini normalize et
      const normalizedUsers = data.map(user => ({
        ...user,
        role: normalizeRole(user.role),
      }));
      setUsers(normalizedUsers);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Kullanıcılar yüklenirken bir hata oluştu');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadUsers();
  }, [loadUsers, refreshTrigger]);

  useEffect(() => {
    if (!autoRefresh) return;

    const interval = setInterval(() => {
      loadUsers();
    }, 60000); // 60 saniye

    return () => clearInterval(interval);
  }, [autoRefresh, loadUsers]);

  const handleCreate = () => {
    navigate('/users/new');
  };

  const handleEdit = (id: string) => {
    navigate(`/users/${id}/edit`);
  };

  const handleManagePermissions = (id: string) => {
    navigate(`/users/${id}/permissions`);
  };

  const handleDeleteClick = (user: User) => {
    setSelectedUser(user);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!selectedUser) return;

    try {
      await userService.delete(selectedUser.id);
      await loadUsers();
      setDeleteDialogOpen(false);
      setSelectedUser(null);
    } catch (err: any) {
      alert(err.response?.data?.message || 'Kullanıcı silinirken bir hata oluştu');
    }
  };

  if (loading) {
    return (
      <Layout>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
          <CircularProgress />
        </Box>
      </Layout>
    );
  }

  return (
    <Layout>
      <Box
        sx={{
          position: 'relative',
          opacity: isRefreshing ? 0.7 : 1,
          transition: 'opacity 0.3s ease-in-out',
          pointerEvents: isRefreshing ? 'none' : 'auto',
        }}
      >
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4" component="h1">
          Kullanıcılar
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={handleCreate}
        >
          Yeni Kullanıcı
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {users.length === 0 ? (
        <Paper sx={{ p: 4, textAlign: 'center', ...getRefreshPaperStyles(isRefreshing) }}>
          <Typography variant="body1" color="text.secondary" gutterBottom>
            Henüz kullanıcı bulunmuyor.
          </Typography>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleCreate}
            sx={{ mt: 2 }}
          >
            İlk Kullanıcıyı Oluştur
          </Button>
        </Paper>
      ) : (
        <Paper
          sx={{
            borderRadius: 3,
            boxShadow: '0px 1px 3px rgba(0, 0, 0, 0.08), 0px 1px 2px rgba(0, 0, 0, 0.04)',
            overflow: 'hidden',
            ...getRefreshPaperStyles(isRefreshing),
          }}
        >
          <TableContainer sx={{ borderRadius: 2, border: '1px solid', borderColor: 'divider' }}>
            <Table size="small" sx={getRefreshTableStyles(isRefreshing)}>
              <TableHead>
                <TableRow sx={{ bgcolor: (theme) => theme.palette.mode === 'dark' ? theme.palette.action.hover : theme.palette.grey[50] }}>
                  <TableCell sx={{ fontWeight: 600, fontSize: '0.8rem', py: 1.5, borderBottom: '2px solid', borderColor: 'divider' }}>
                    <Box display="flex" alignItems="center" gap={0.75}>
                      <PersonIcon sx={{ fontSize: 18, color: 'primary.main' }} />
                      Kullanıcı Adı
                    </Box>
                  </TableCell>
                  <TableCell sx={{ fontWeight: 600, fontSize: '0.8rem', py: 1.5, borderBottom: '2px solid', borderColor: 'divider' }}>
                    <Box display="flex" alignItems="center" gap={0.75}>
                      <EmailIcon sx={{ fontSize: 18, color: 'primary.main' }} />
                      E-posta
                    </Box>
                  </TableCell>
                  <TableCell sx={{ fontWeight: 600, fontSize: '0.8rem', py: 1.5, borderBottom: '2px solid', borderColor: 'divider' }}>Rol</TableCell>
                  <TableCell sx={{ fontWeight: 600, fontSize: '0.8rem', py: 1.5, borderBottom: '2px solid', borderColor: 'divider' }}>
                    <Box display="flex" alignItems="center" gap={0.75}>
                      <ScheduleIcon sx={{ fontSize: 18, color: 'primary.main' }} />
                      Oluşturulma Tarihi
                    </Box>
                  </TableCell>
                  <TableCell sx={{ fontWeight: 600, fontSize: '0.8rem', py: 1.5, borderBottom: '2px solid', borderColor: 'divider' }} align="right">İşlemler</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {users.map((user, index) => (
                  <TableRow
                    key={user.id}
                    hover
                    sx={{
                      bgcolor: index % 2 === 1 ? 'action.hover' : 'transparent',
                      '&:hover': { bgcolor: 'action.selected' },
                      transition: 'background-color 0.15s',
                    }}
                  >
                    <TableCell sx={{ fontSize: '0.8rem', py: 1.25 }}>
                      <Box display="flex" alignItems="center" gap={0.75}>
                        <PersonIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                        {user.username}
                      </Box>
                    </TableCell>
                    <TableCell sx={{ fontSize: '0.8rem', py: 1.25 }}>
                      <Box display="flex" alignItems="center" gap={0.75}>
                        <EmailIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                        {user.email}
                      </Box>
                    </TableCell>
                    <TableCell sx={{ fontSize: '0.8rem', py: 1.25 }}>
                      <Chip
                        label={roleLabels[user.role]}
                        size="small"
                        sx={{ height: 22, fontSize: '0.75rem' }}
                        color={
                          user.role === UserRoleEnum.SystemAdmin
                            ? 'error'
                            : user.role === UserRoleEnum.TenantAdmin
                            ? 'warning'
                            : 'default'
                        }
                      />
                    </TableCell>
                    <TableCell sx={{ fontSize: '0.8rem', py: 1.25 }}>
                      <Box display="flex" alignItems="center" gap={0.75}>
                        <ScheduleIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                        {formatDateToTR(user.createdAt)}
                      </Box>
                    </TableCell>
                    <TableCell sx={{ fontSize: '0.8rem', py: 1.25 }} align="right">
                      <IconButton size="small" onClick={() => handleEdit(user.id)} color="primary" aria-label="Düzenle">
                        <EditIcon />
                      </IconButton>
                      <IconButton size="small" onClick={() => handleManagePermissions(user.id)} color="success" aria-label="Yetkiler">
                        <SecurityIcon />
                      </IconButton>
                      <IconButton size="small" onClick={() => handleDeleteClick(user)} color="error" aria-label="Sil">
                        <DeleteIcon />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}

      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
      >
        <DialogTitle>Kullanıcı Sil</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {selectedUser?.username} adlı kullanıcıyı silmek istediğinize emin misiniz? Bu işlem geri alınamaz.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>İptal</Button>
          <Button onClick={handleDeleteConfirm} color="error" variant="contained">
            Sil
          </Button>
        </DialogActions>
      </Dialog>
      </Box>
    </Layout>
  );
};

export default Users;

